import type { ProfileUpdateRequest } from '../services/api';

type ProfilePageProfileLike = ProfileUpdateRequest & {
    user_id?: string;
    user_metadata?: Record<string, unknown>;
};

export const PROFILE_EDITABLE_FIELDS = [
    'given_name',
    'family_name',
    'name',
    'email',
    'picture',
] as const;

export function hasEditableProfileField(restrictedFields: string[]): boolean {
    return PROFILE_EDITABLE_FIELDS.some((field) => !restrictedFields.includes(field));
}

const samlRestrictedFields = ['given_name', 'family_name', 'name', 'email', 'picture'];
const socialRestrictedFields = ['given_name', 'family_name', 'email'];

function addRestrictedField(fields: Set<string>, isMatch: boolean, fieldName: string) {
    if (isMatch) {
        fields.add(fieldName);
    }
}

function shouldIncludeProfileField({
    fieldName,
    newValue,
    currentValue,
    restrictedFields,
}: {
    fieldName: string;
    newValue: string | undefined;
    currentValue: string | undefined;
    restrictedFields: string[];
}): boolean {
    if (restrictedFields.includes(fieldName)) {
        return false;
    }

    if (!newValue || newValue.trim() === '') {
        return false;
    }

    return newValue !== (currentValue || '');
}

export function extractRestrictedFieldsFromProfile(
    userProfile: Pick<ProfilePageProfileLike, 'user_id'> | null | undefined,
): string[] {
    if (!userProfile) {
        return [];
    }

    const userId = userProfile.user_id || '';
    const isSamlUser = userId.includes('samlp|') || userId.includes('google-saml|');
    const isSocialUser =
        userId.includes('google-oauth2|') ||
        userId.includes('facebook|') ||
        userId.includes('linkedin|') ||
        userId.includes('github|') ||
        userId.includes('twitter|') ||
        userId.includes('microsoft|') ||
        userId.includes('apple|');

    if (isSamlUser) {
        return [...samlRestrictedFields];
    }

    if (isSocialUser) {
        return [...socialRestrictedFields];
    }

    return [];
}

export function mergeRestrictedFieldsFromErrorMessage(
    currentFields: string[],
    errorMessage: string,
): string[] {
    const restrictedFields = new Set(currentFields);

    try {
        const errorResponse = JSON.parse(errorMessage) as { restrictedFields?: string[] };
        for (const field of errorResponse.restrictedFields ?? []) {
            restrictedFields.add(field);
        }
    } catch {
        addRestrictedField(restrictedFields, errorMessage.includes('Cannot update email'), 'email');
        addRestrictedField(
            restrictedFields,
            errorMessage.includes('Cannot update given_name') ||
                errorMessage.includes('first name'),
            'given_name',
        );
        addRestrictedField(
            restrictedFields,
            errorMessage.includes('Cannot update family_name') ||
                errorMessage.includes('last name'),
            'family_name',
        );
        addRestrictedField(
            restrictedFields,
            errorMessage.includes('Cannot update name') || errorMessage.includes('full name'),
            'name',
        );
        addRestrictedField(
            restrictedFields,
            errorMessage.includes('Cannot update picture'),
            'picture',
        );
    }

    return [...restrictedFields];
}

export function buildProfileUpdateRequest({
    formData,
    profile,
    restrictedFields,
}: {
    formData: ProfileUpdateRequest;
    profile: ProfilePageProfileLike | null | undefined;
    restrictedFields: string[];
}): ProfileUpdateRequest {
    const updates: ProfileUpdateRequest = {};

    if (
        shouldIncludeProfileField({
            fieldName: 'given_name',
            newValue: formData.given_name,
            currentValue: profile?.given_name,
            restrictedFields,
        })
    ) {
        updates.given_name = formData.given_name;
    }
    if (
        shouldIncludeProfileField({
            fieldName: 'family_name',
            newValue: formData.family_name,
            currentValue: profile?.family_name,
            restrictedFields,
        })
    ) {
        updates.family_name = formData.family_name;
    }
    if (
        shouldIncludeProfileField({
            fieldName: 'name',
            newValue: formData.name,
            currentValue: profile?.name,
            restrictedFields,
        })
    ) {
        updates.name = formData.name;
    }
    if (
        shouldIncludeProfileField({
            fieldName: 'email',
            newValue: formData.email,
            currentValue: profile?.email,
            restrictedFields,
        })
    ) {
        updates.email = formData.email;
    }
    if (
        shouldIncludeProfileField({
            fieldName: 'picture',
            newValue: formData.picture,
            currentValue: profile?.picture,
            restrictedFields,
        })
    ) {
        updates.picture = formData.picture;
    }

    if (
        formData.user_metadata &&
        JSON.stringify(formData.user_metadata) !== JSON.stringify(profile?.user_metadata || {})
    ) {
        updates.user_metadata = formData.user_metadata;
    }

    return updates;
}
